import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, Check, X, GraduationCap } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

interface ProcessedInvitation {
  id: string;
  classroom_id: string;
  student_id: string;
  status: string;
  sent_at: string;
  classroom: {
    name: string;
    teacher: {
      username: string;
      email: string;
    };
  };
}

export default function ClassroomInvitations() {
  const { user } = useAuth();
  const [invitations, setInvitations] = useState<ProcessedInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;

    // Request notification permission
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }

    // Initial fetch of pending invitations
    const loadInvitations = async () => {
      try {
        // First, get current user's email and username
        const { data: userData } = await supabase
          .from("users")
          .select("email, username")
          .eq("id", user.id)
          .single();

        // Query invitations that match by student_id OR email OR username
        const { data: invitationsData, error } = await supabase
          .from("classroom_invitations")
          .select("id, classroom_id, student_id, status, sent_at")
          .eq("status", "pending")
          .or(
            `student_id.eq.${user.id},student_email.eq.${userData?.email},student_username.eq.${userData?.username}`,
          )
          .order("sent_at", { ascending: false });

        if (error) {
          console.error("Failed to load classroom invitations:", error);
          setInvitations([]);
          return;
        }

        if (!invitationsData || invitationsData.length === 0) {
          setInvitations([]);
          return;
        }

        // Get unique classroom IDs
        const classroomIds = [
          ...new Set(invitationsData.map((inv) => inv.classroom_id)),
        ];

        // Fetch classroom details
        const { data: classroomsData, error: classroomsError } = await supabase
          .from("classrooms")
          .select("id, name, teacher_id")
          .in("id", classroomIds);

        if (classroomsError) {
          console.error("Failed to load classrooms:", classroomsError);
        }

        // Create classroom map
        const classroomsMap = new Map(
          (classroomsData || []).map((c) => [c.id, c]),
        );

        // Get unique teacher IDs
        const teacherIds = [
          ...new Set(
            (classroomsData || []).map((c) => c.teacher_id).filter(Boolean),
          ),
        ];

        console.log("Classrooms data:", classroomsData);
        console.log("Teacher IDs to fetch:", teacherIds);

        // Fetch teacher information
        const { data: teachersData, error: teachersError } = await supabase
          .from("users")
          .select("id, username, email")
          .in("id", teacherIds);

        console.log("Teachers query result:", { teachersData, teachersError });
        console.log("Number of teachers found:", teachersData?.length || 0);

        if (teachersError) {
          console.error("Failed to load teachers:", teachersError);
        }

        // Create teacher map
        const teachersMap = new Map((teachersData || []).map((t) => [t.id, t]));

        // Transform data
        const processedData: ProcessedInvitation[] = invitationsData.map(
          (inv) => {
            const classroom = classroomsMap.get(inv.classroom_id);
            const teacher = classroom?.teacher_id
              ? teachersMap.get(classroom.teacher_id)
              : null;

            return {
              id: inv.id,
              classroom_id: inv.classroom_id,
              student_id: inv.student_id,
              status: inv.status,
              sent_at: inv.sent_at,
              classroom: {
                name: classroom?.name || "Unknown Classroom",
                teacher: {
                  username: teacher?.username || "Unknown Teacher",
                  email: teacher?.email || "",
                },
              },
            };
          },
        );

        setInvitations(processedData);
      } catch (error) {
        console.error("Failed to load classroom invitations:", error);
        setInvitations([]);
      } finally {
        setLoading(false);
      }
    };

    loadInvitations();

    // Subscribe to real-time changes for new invitations
    // Note: Real-time filters are limited, so we subscribe to all status='pending'
    // and filter client-side
    const channel = supabase
      .channel("classroom-invitations-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "classroom_invitations",
          filter: `status=eq.pending`,
        },
        (payload) => {
          console.log("New classroom invitation received:", payload);
          // Reload to get classroom details and filter properly
          loadInvitations();
          // Optional: Show browser notification
          if (
            "Notification" in window &&
            Notification.permission === "granted"
          ) {
            new Notification("New Classroom Invitation", {
              body: "You have been invited to join a classroom",
              icon: "/favicon.png",
            });
          }
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "classroom_invitations",
          filter: `status=eq.pending`,
        },
        (payload) => {
          console.log("Classroom invitation updated:", payload);
          // Reload to get updated data
          loadInvitations();
        },
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const respondToInvitation = async (
    invitationId: string,
    action: "accept" | "reject",
  ) => {
    setResponding(invitationId);

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const token = session?.access_token;

      if (!token) throw new Error("Not authenticated");

      const SUPABASE_URL =
        import.meta.env.VITE_SUPABASE_URL || "http://localhost:54321";
      const response = await fetch(
        `${SUPABASE_URL}/functions/v1/respond-to-classroom-invitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ invitation_id: invitationId, action }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to respond to invitation");
      }

      // Remove the invitation from the list
      setInvitations((prev) => prev.filter((inv) => inv.id !== invitationId));

      // Show success message
      alert(data.message || `Invitation ${action}ed successfully!`);
    } catch (error) {
      console.error("Failed to respond to invitation:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to respond to invitation",
      );
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200 bg-purple-50">
        <CardContent className="p-4">
          <p className="text-sm text-gray-500">
            Loading classroom invitations...
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show debug info when no invitations (temporarily for debugging)
  if (invitations.length === 0) {
    return (
      <Card className="border-2 border-gray-200 bg-gray-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-700">
            <Bell className="w-5 h-5" />
            Classroom Invitations (0)
          </CardTitle>
          <CardDescription className="text-gray-600">
            No pending classroom invitations.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-purple-200 bg-purple-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-purple-900">
          <Bell className="w-5 h-5" />
          Classroom Invitations ({invitations.length})
        </CardTitle>
        <CardDescription className="text-purple-700">
          You have been invited to join these classrooms
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {invitations.map((invitation) => (
          <Alert key={invitation.id} className="bg-white">
            <GraduationCap className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <div className="flex-grow">
                <p className="font-semibold">{invitation.classroom.name}</p>
                <p className="text-sm text-gray-600">
                  Teacher: {invitation.classroom.teacher.username} (
                  {invitation.classroom.teacher.email})
                </p>
                <p className="text-xs text-gray-500">
                  Invited {new Date(invitation.sent_at).toLocaleDateString()}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="default"
                  onClick={() => respondToInvitation(invitation.id, "accept")}
                  disabled={responding === invitation.id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-1" />
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => respondToInvitation(invitation.id, "reject")}
                  disabled={responding === invitation.id}
                >
                  <X className="w-4 h-4 mr-1" />
                  Reject
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        ))}
      </CardContent>
    </Card>
  );
}
